import { useCallback } from 'react';
import {
  SETTINGS_DELETE_MODAL_TYPE,
  SETTINGS_DELETE_NAMED_COPY_KEYS,
  SETTINGS_DELETE_UNNAMED_COPY_KEYS
} from 'constants/settings';
import { SETTINGS_DELETE_TARGET } from 'enums';
import { ConfirmActionModalType } from 'interfaces/modal.interface';
import { t } from 'services/i18n.service';
import { useAppDispatch } from 'store/hooks';
import { updateConfirmActionModal } from 'store/reducer/modal/slice';

interface ConfirmHabitDeletionParams {
  target: SETTINGS_DELETE_TARGET.HABIT;
  name: string;
  type: string;
  position: number;
  customRoutinePosition?: number;
}

interface ConfirmCustomRoutineDeletionParams {
  target: SETTINGS_DELETE_TARGET.CUSTOM_ROUTINE;
  name: string;
  position: number;
}

export type ConfirmSettingsDeletionParams =
  ConfirmHabitDeletionParams | ConfirmCustomRoutineDeletionParams;

const getDeletionDescription = (
  target: SETTINGS_DELETE_TARGET,
  name: string
): string =>
  name.length > 0
    ? t(SETTINGS_DELETE_NAMED_COPY_KEYS[target], { name })
    : t(SETTINGS_DELETE_UNNAMED_COPY_KEYS[target]);

const getDeletionModalData = (
  params: ConfirmSettingsDeletionParams
): ConfirmActionModalType['data'] => {
  if (params.target === SETTINGS_DELETE_TARGET.HABIT) {
    return {
      type: params.type,
      position: params.position,
      customRoutinePosition: params.customRoutinePosition
    };
  }

  return { position: params.position };
};

const buildSettingsDeletionModal = (
  params: ConfirmSettingsDeletionParams
): ConfirmActionModalType => ({
  showConfirmModal: true,
  modalType: SETTINGS_DELETE_MODAL_TYPE[params.target],
  confirmTitle: t('settings_page.delete'),
  cancelTitle: t('btn_cancel'),
  description: getDeletionDescription(params.target, params.name),
  data: getDeletionModalData(params)
});

const useConfirmSettingsDeletion = (params: ConfirmSettingsDeletionParams) => {
  const dispatch = useAppDispatch();

  return useCallback(() => {
    dispatch(updateConfirmActionModal(buildSettingsDeletionModal(params)));
  }, [dispatch, params]);
};

export default useConfirmSettingsDeletion;
